<script lang="ts">
export default {
    name: 'BaseStatusBarFold',
};
</script>

<script setup lang="ts">
import { watch, computed, ref } from 'vue-demi';

import FoldStatus from './components/fold-status.vue';
import { useScroll, useTopGap } from './hooks';

interface Props {
    /**
     * 标题
     */
    title?: string;
    /**
     * 是否展示返回
     */
    showBack?: boolean;
    /**
     * 背景颜色
     */
    background?: string;
    /**
     * 图标以及标题的颜色
     */
    iconColor?: string;
    /**
     * 切换后背景的颜色，如果设定则默认启用滑动切换状态
     */
    switchBackground?: string;
    /**
     * 切换后背景以及标题的颜色，如果设定则默认启用滑动切换状态
     */
    switchIconColor?: string;
    /**
     * 触发滚动事件的阈值
     */
    scrollThreshold?: number;
    /**
     * 滚动切换临界值
     */
    scrollEdge?: number;
    /**
     * 返回前的执行回调
     */
    beforeBack?: (() => void) | (() => Promise<void>);
    /**
     * 自定义返回事件
     */
    customBack?: () => void;
    /**
     * 是否展示分享
     */
    showShare?: boolean;
    /**
     * 手动指定statusBarHeight
     */
    statusBarHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
    title: '',
    background: '#fff',
    showBack: true,
    switchBackground: '',
    switchIconColor: '',
    iconColor: '',
    showShare: true,
    scrollThreshold: 10,
    scrollEdge: 100,
});

const emit = defineEmits<{
    /**
     * 分享
     */
    (event: 'share'): void;
    /**
     * 折叠状态栏顶导高度
     * @arg { number } val
     */
    (event: 'total-height', val: number): void;
    /**
     * 系统状态栏顶导高度
     * @arg { number } val
     */
    (event: 'status-bar-height', val: number): void;
    /**
     * 透明度变化
     * @arg { number } val
     */
    (event: 'opacity', val: number): void;
}>();

const shouldListenScroll = computed(() => Boolean(props.switchBackground) || Boolean(props.switchIconColor));
const { opacityValue, switchShowStyle, switchHideStyle, listenScroll, removeScroll } = useScroll(
    props.scrollThreshold,
    props.scrollEdge,
);

watch(
    () => shouldListenScroll.value,
    (val) => {
        if (val) {
            listenScroll();
        } else {
            removeScroll();
        }
    },
    {
        immediate: true,
    },
);

watch(opacityValue, (val) => {
    emit('opacity', 1 - val);
});

const { topGap, statusBarHeight } = useTopGap(props.statusBarHeight);

const barHolder = ref<HTMLElement | null>(null);

watch(
    barHolder,
    (val) => {
        if (val) {
            const elRect = val?.getBoundingClientRect();
            const height = elRect?.height ?? 0;
            const displayHeight = Math.floor(height * 2) / 2;
            emit('total-height', displayHeight);
        }
    },
    {
        flush: 'post',
    },
);

emit('status-bar-height', statusBarHeight);
</script>

<template>
    <nav class="status-bar">
        <div ref="barHolder" class="status-bar-placeholder" :style="topGap"></div>
        <div class="status-bar-main">
            <FoldStatus
                :title="title"
                :background="background"
                :icon-color="iconColor"
                :show-back="showBack"
                :show-share="showShare"
                :style="[topGap, shouldListenScroll && switchShowStyle]"
                :before-back="beforeBack"
                :custom-back="customBack"
                class="status-bar-fold"
                @share="emit('share')"
            >
                <template v-if="$slots['left-fold']" #left-fold>
                    <!-- @slot 左侧 -->
                    <slot name="left-fold" />
                </template>
                <template v-if="$slots['title-fold']" #title-fold>
                    <!-- @slot 标题 -->
                    <slot name="title-fold" />
                </template>
                <template v-if="$slots['right-fold']" #right-fold>
                    <!-- @slot 右侧 -->
                    <slot name="right-fold" />
                </template>
            </FoldStatus>
            <FoldStatus
                v-if="shouldListenScroll"
                :title="title"
                :background="switchBackground"
                :icon-color="switchIconColor"
                :show-share="showShare"
                :show-back="showBack"
                :style="[topGap, switchHideStyle]"
                :before-back="beforeBack"
                :custom-back="customBack"
                class="status-bar-switch"
                @share="emit('share')"
            >
                <template v-if="$slots['left-switch-fold'] || $slots['left-fold']" #left-fold>
                    <template v-if="$slots['left-switch-fold']">
                        <!-- @slot 切换后左侧 -->
                        <slot name="left-switch-fold" />
                    </template>
                    <template v-else>
                        <slot name="left-fold" />
                    </template>
                </template>
                <template v-if="$slots['title-switch-fold'] || $slots['title-fold']" #title-fold>
                    <template v-if="$slots['title-switch-fold']">
                        <!-- @slot 切换后标题 -->
                        <slot name="title-switch-fold" />
                    </template>
                    <template v-else>
                        <slot name="title-fold" />
                    </template>
                </template>
                <template v-if="$slots['right-switch-fold'] || $slots['right-fold']" #right-fold>
                    <template v-if="$slots['right-switch-fold']">
                        <!-- @slot 切换后右侧 -->
                        <slot name="right-switch-fold" />
                    </template>
                    <template v-else>
                        <slot name="right-fold" />
                    </template>
                </template>
            </FoldStatus>
        </div>
    </nav>
</template>

<style lang="scss" scoped>
@import './var.scss';

.status-bar-main {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 999;
    width: 100%;
    max-width: $status-bar-max-width;
    margin: auto;
}

.status-bar-placeholder {
    height: calc($fold-item-size + 2 * $fold-vertical-gap);
    box-sizing: content-box;
}
</style>
